'use strict';

import { flow, observable, configure, action, observe, makeObservable } from "mobx";
import {deepObserve} from "mobx-utils";
configure({enforceActions: 'always'});

import appStore from '@uw-it-sis/lib-react/lib/AppStore';

import _ from 'underscore';
import {States} from '@uw-it-sis/lib-react/lib/AppConstants';
import {getAggregateState} from '@uw-it-sis/lib-react/lib/StateUtils';
import bookmarksStore from '@uw-it-sis/lib-react-myplan/lib/BookmarksStore';
import {normalize} from "@uw-it-sis/lib-react-myplan/lib/CourseUtils";

import events from "@uw-it-sis/lib-react/lib/Events";
import PlanEvents from '@uw-it-sis/lib-react-myplan/lib/PlanEvents';

/**
 * Store for course bookmarks.
 *
 * This store works with the BookmarksStore to get bookmarks data, which it then uses to download relevant course data.
 */
class BookmarksPageStore {

    state;

    constructor() {
        makeObservable(this, {
            state: observable,
            checkDataLoadStates: action
        });

        this.courses = [];
        this.plannedCourses = {};
        this.courseMessages = {};
        this.exceptionUtils = appStore.getExceptionUtils();

        events.listen(PlanEvents.planItemAdded, (planItem) => this.onPlanItemAdded(planItem));

        // This is the object to track the status of the loads.
        this.dataLoadStates = {
            courses: States.initial,
            courseMessages: States.initial
        }
    }

    /**
     * Cleans things up when a store goes out of play.
     */
    unmount() {
        // Only dispose if the user is authenticated.
        if (appStore.isAuthenticated()) {
            this.stateDisposer();
            this.bookmarksDisposer();
        }
        events.remove(PlanEvents.planItemAdded, (planItem) => this.onPlanItemAdded(planItem));
    }

    /**
     * Initialize by loading bookmarks data and corresponding course data.
     */
    mount = flow(function* () { // This a generator function!

        // Don't try to load bookmarks for unauthenticated users.
        if (! appStore.isAuthenticated()) {
             this.state = States.done;
             return;
        }

        /*
         * Observe BookmarkStore.state so that this store knows when a bookmarks data load has happened and reload
         * the corresponding course data.
         */
        this.stateDisposer = observe(bookmarksStore, "state", (change) => {
            if (change.newValue === States.done) {
                if (appStore.isDebug()) {
                   console.log("BookmarksPageStore: Observed BookmarksStore.state change to 'done'. Reloading courses.");
                }
                this._loadData(this._getCourseCodesFromBookmarks());
            }
        });

        /*
         * Observe changes to the bookmarksStore.bookmarks data and reload course data. Have to use deepObserve to see
         * adds and removes within the bookmarks map.
         */
        this.bookmarksDisposer = deepObserve(bookmarksStore, (change, path) => {
            if (path === "bookmarks") {
                if (appStore.isDebug()) {
                    console.log('BookmarksPageStore: Observed BookmarksStore.bookmarks changed. Reloading courses.');
                }
                this._loadData(this._getCourseCodesFromBookmarks());
            }
        });

        this.state = States.pending;
        // Load bookmarks data.
        bookmarksStore.loadBookmarks();
    });

    _loadData(cCodes) {
        // Update the aggregate state to pending.
        this.state = States.pending;
        this._loadCourseData(cCodes);
        this._loadCourseMessages(cCodes);
    }

    /**
     * Courses for bookmarks data.
     */
    _loadCourseData = flow(function* (cCodes) { // This a generator function!
        this.dataLoadStates.courses = States.pending;

        if (cCodes.length === 0) {
            // If there are no course codes then clear out the courses array and be done.
            this.courses = [];
            this.dataLoadStates.courses = States.done;
            this.checkDataLoadStates();
            return;
        }

        /*
         * Now load the course data for the given course codes.
         */
        const url = "api/courses?courseCodes=" + encodeURIComponent(cCodes.join(","));

        try {
            this.courses = yield appStore.getHttpClient().doGet(url, 'getCourses');

            // Sort the results.
            this._sort();
            this.dataLoadStates.courses = States.done;
        } catch(error) {
            this.exceptionUtils.handleException(error);
            this.dataLoadStates.courses = States.error;
        } finally {
            this.checkDataLoadStates();
        }
    });

    /**
     * CRUD messages for bookmarked courses
     */
    _loadCourseMessages = flow(function* (cCodes) { // This a generator function!
        this.dataLoadStates.courseMessages = States.pending;

        if (cCodes.length === 0) {
            // If there are no course codes then clear out the courses array and be done.
            this.courseMessages = {};
            this.dataLoadStates.courseMessages = States.done;
            this.checkDataLoadStates();
            return;
        }

        /*
         * Now load the course messages for the given course codes.
         */
        let url = sprintf("api/coursemessages?courseCodes=%s", encodeURIComponent(cCodes));
        try {
            this.courseMessages = yield appStore.getHttpClient().doGet(url, 'getCourseMessages');
            if (appStore.isDebug()) {
                console.log(sprintf("BookmarksPageStore[%s]: Course messages load complete.", this.courseCode));
            }
            this.dataLoadStates.courseMessages = States.done;
        } catch(error) {
            this.exceptionUtils.handleException(error);
            this.dataLoadStates.courseMessages = States.error;
        } finally {
            this.checkDataLoadStates();
        }
    });

    /**
     * Gets the course bookmarks from the BookmarksStore and pulls out the course codes.
     *
     * @return array An array of course codes if there were any bookmarks. Otherwise, an empty array.
     * @private
     */
    _getCourseCodesFromBookmarks() {
        let courseCodes = [];
        // Push the codes into the return array, filtering out any non-course bookmarks.
        _(bookmarksStore.bookmarks).each(function(element){
            if (element.type === "kuali.lu.type.CreditCourse") {
                courseCodes.push(element.code);
            }
        });

        return courseCodes;
    }

    /**
     * Order the courses by the createTime of the corresponding bookmark, newest at the top.
     */
    _sort() {
        let bookmarks = bookmarksStore.getBookmarks();
        // To sort observable arrays you have to call splice before sort.
        this.courses.slice().sort(function(p1, p2) {
            // Course codes are padded to 8 characters in the course data, but only have a single space in the
            // bookmarks data. So, the extra spaces have to be removed to look them up.
            let courseCode1 = p1.code.replace(/\s+/g, ' ');
            let courseCode2 =  p2.code.replace(/\s+/g, ' ');

            let bm1 = bookmarks[courseCode1];
            let bm2 = bookmarks[courseCode2];

            // Get the difference between the two times and use that to order newest at the top.
            let diff = bm1.createTime - bm2.createTime;
            if (diff > 0) {
                return -1;
            }
            if (diff < 0) {
                return 1;
            }
            return 0;
        });
    }

    onPlanItemAdded(eventObject) {
        // Ignore section adds/removes ... as sections can't be bookmarked.
        if (! eventObject.isSection) {
            const courseCode = normalize(eventObject.code);
            let courseCodes = this._getCourseCodesFromBookmarks();
            if (courseCodes.includes(courseCode)) {
                // Update the aggregate state to pending.
                this.state = States.pending;
                this._loadCourseMessages(courseCodes);
            }
        }
    }

    /**
     * Update state if necessary.
     */
    checkDataLoadStates() {
        this.state = getAggregateState(this.dataLoadStates);
    }
}

export default new BookmarksPageStore();